import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const ADD = "__add_new__";

// Select with a "+ Add new…" option that creates the item via `endpoint` and selects it.
export const AddableSelect = ({ value, onChange, items, onCreated, endpoint, noun, testid, placeholder = "Select", noneLabel }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const pick = (v) => {
    if (v === ADD) { setName(""); setOpen(true); return; }
    onChange(v === "__none__" ? "" : v);
  };

  const save = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      const { data } = await api.post(endpoint, { name: n });
      onCreated(data);
      onChange(data.name);
      toast.success(`${noun} "${data.name}" added`);
      setOpen(false);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Select value={value || (noneLabel ? "__none__" : "")} onValueChange={pick}>
        <SelectTrigger data-testid={testid} className="mt-1"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {noneLabel && <SelectItem value="__none__">{noneLabel}</SelectItem>}
          {items.map((i) => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}
          <SelectSeparator />
          <SelectItem value={ADD} data-testid={`${testid}-add-new`} className="text-emerald-700 font-medium">
            <span className="inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add new {noun.toLowerCase()}…</span>
          </SelectItem>
        </SelectContent>
      </Select>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`${testid}-add-dialog`}>
          <DialogHeader><DialogTitle>Add new {noun.toLowerCase()}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Input data-testid={`${testid}-add-input`} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={`${noun} name`} maxLength={noun === "Entity" ? 100 : 50} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-add-cancel`}>Cancel</Button>
              <Button type="submit" disabled={saving || !name.trim()} data-testid={`${testid}-add-save`} className="bg-blue-700 hover:bg-blue-800 text-white">
                {saving ? "Adding…" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
